import {
  BatteryCharging,
  BatteryFull,
  CarFront,
  Factory,
  Flame,
  PlugZap,
  Sun,
  type LucideIcon,
} from "lucide-react";

export type CustomerType = "private" | "business";

export type QuoteFieldType = "select" | "radio" | "multi" | "number" | "text";

export interface QuoteField {
  /** Unikt id, prefixas automatiskt med tjänstens slug i metadatan */
  id: string;
  label: string;
  type: QuoteFieldType;
  options?: string[];
  placeholder?: string;
  unit?: string;
  required?: boolean;
  /** Visa endast om ett annat fält i samma tjänst har ett av dessa värden */
  showIf?: { field: string; values: string[] };
  /** Visa endast för dessa kundtyper (default: alla) */
  customerTypes?: CustomerType[];
  /** Alternativ etikett för företag/BRF */
  businessLabel?: string;
  /** Alternativa svarsalternativ för företag/BRF */
  businessOptions?: string[];
  /** Alternativ placeholder för företag/BRF */
  businessPlaceholder?: string;
}

export interface QuoteService {
  slug: string;
  label: string;
  shortLabel: string;
  description: string;
  icon: LucideIcon;
  customerTypes: CustomerType[];
  leadCategory: string;
  fields: QuoteField[];
}

export const quoteServices: QuoteService[] = [
  {
    slug: "solceller",
    label: "Solceller",
    shortLabel: "Solceller",
    description: "Solcellsanläggning för villa, radhus eller fritidshus.",
    icon: Sun,
    customerTypes: ["private", "business"],
    leadCategory: "solceller",
    fields: [
      { id: "installationType", label: "Ny eller befintlig installation?", type: "radio", options: ["Ny installation", "Komplettera befintlig"], required: true, businessLabel: "Ny eller befintlig anläggning?" },
      { id: "placement", label: "Var ska solcellerna installeras?", type: "select", options: ["På taket", "På marken", "Carport/garage", "Annat"], required: true, businessOptions: ["På taket", "På marken", "Parkering/carport", "Fasad", "Annat"] },
      { id: "roofType", label: "Välj taktyp", type: "select", options: ["Tegel- eller betongpannor", "Plåttak", "Papptak", "Eternit", "Vet ej"], showIf: { field: "placement", values: ["På taket"] } },
      { id: "propertyType", label: "Fastighetstyp", type: "select", options: ["Villa", "Radhus", "Fritidshus", "Lantbruk"], required: true, businessLabel: "Typ av fastighet", businessOptions: ["Flerbostadshus/BRF", "Kontor", "Industri/lager", "Handel", "Lantbruk", "Annan verksamhet"] },
      { id: "ownership", label: "Äger ni fastigheten?", type: "radio", options: ["Ja", "Nej, hyresgäst"], required: true, customerTypes: ["business"] },
      { id: "consumption", label: "Ungefärlig årsförbrukning", type: "number", unit: "kWh/år", placeholder: "t.ex. 15000", businessPlaceholder: "t.ex. 250000" },
      { id: "financing", label: "Önskad finansiering", type: "select", options: ["Köp", "Leasing", "Solel som tjänst (PPA)", "Vet ej"], customerTypes: ["business"] },
      { id: "timeline", label: "När vill du installera?", type: "select", options: ["Snarast", "Inom 3 månader", "Inom 6 månader", "Bara nyfiken på pris"], required: true, businessLabel: "När vill ni installera?", businessOptions: ["Snarast", "Inom 3 månader", "Inom 6 månader", "Utreder pris"] },
    ],
  },
  {
    slug: "hembatterier",
    label: "Hembatterier",
    shortLabel: "Hembatteri",
    description: "Batterilager som lagrar solel och kapar dyra timmar.",
    icon: BatteryCharging,
    customerTypes: ["private", "business"],
    leadCategory: "hembatteri",
    fields: [
      { id: "hasSolar", label: "Har du solceller idag?", type: "radio", options: ["Ja", "Nej", "Planerar att installera"], required: true, businessLabel: "Har ni solceller idag?" },
      { id: "capacity", label: "Önskad batterikapacitet", type: "select", options: ["Under 10 kWh", "10–15 kWh", "15–25 kWh", "Över 25 kWh", "Vet ej – föreslå gärna"], required: true, businessOptions: ["Under 25 kWh", "25–50 kWh", "50–100 kWh", "Över 100 kWh", "Vet ej – föreslå gärna"] },
      { id: "purpose", label: "Vad är syftet?", type: "multi", options: ["Lagra egen solel", "Kapa dyra timmar", "Reservkraft vid strömavbrott", "Stödtjänster/intäkter"], businessOptions: ["Lagra egen solel", "Effekttoppskapning", "Reservkraft", "Stödtjänster (FCR)", "Undvika nätförstärkning"] },
      { id: "gridPower", label: "Abonnerad effekt", type: "number", unit: "kW", placeholder: "t.ex. 100", customerTypes: ["business"] },
      { id: "consumption", label: "Ungefärlig årsförbrukning", type: "number", unit: "kWh/år", placeholder: "t.ex. 15000", businessPlaceholder: "t.ex. 250000" },
      { id: "timeline", label: "När vill du installera?", type: "select", options: ["Snarast", "Inom 3 månader", "Inom 6 månader", "Bara nyfiken på pris"], required: true, businessLabel: "När vill ni installera?", businessOptions: ["Snarast", "Inom 3 månader", "Inom 6 månader", "Utreder pris"] },
    ],
  },
  {
    slug: "varmepumpar",
    label: "Värmepumpar",
    shortLabel: "Värmepump",
    description: "Luft/vatten, bergvärme eller frånluftsvärmepump.",
    icon: Flame,
    customerTypes: ["private", "business"],
    leadCategory: "varmepump",
    fields: [
      { id: "heatingToday", label: "Hur värmer du huset idag?", type: "select", options: ["Direktverkande el", "Vattenburen el", "Olja", "Fjärrvärme", "Ved/pellets", "Äldre värmepump"], required: true, businessLabel: "Hur värms fastigheten idag?", businessOptions: ["Direktverkande el", "Vattenburen el", "Olja", "Fjärrvärme", "Pellets/biobränsle", "Äldre värmepump", "Blandat"] },
      { id: "pumpType", label: "Vilken typ av värmepump är du intresserad av?", type: "select", options: ["Luft/vatten", "Bergvärme", "Frånluft", "Luft/luft", "Vet ej – föreslå gärna"], required: true, businessLabel: "Vilken typ av värmepump är ni intresserade av?", businessOptions: ["Luft/vatten", "Bergvärme", "Frånluft", "Vet ej – föreslå gärna"] },
      { id: "livingArea", label: "Boyta", type: "number", unit: "m²", placeholder: "t.ex. 140", required: true, businessLabel: "Uppvärmd yta", businessPlaceholder: "t.ex. 1200" },
      { id: "propertyType", label: "Typ av fastighet", type: "select", options: ["Flerbostadshus/BRF", "Kontor", "Industri/lager", "Handel", "Annan verksamhet"], required: true, customerTypes: ["business"] },
      { id: "consumption", label: "Ungefärlig årsförbrukning", type: "number", unit: "kWh/år", placeholder: "t.ex. 20000", businessPlaceholder: "t.ex. 200000" },
      { id: "timeline", label: "När vill du installera?", type: "select", options: ["Snarast", "Inom 3 månader", "Inom 6 månader", "Bara nyfiken på pris"], required: true, businessLabel: "När vill ni installera?", businessOptions: ["Snarast", "Inom 3 månader", "Inom 6 månader", "Utreder pris"] },
    ],
  },
  {
    slug: "laddboxar",
    label: "Laddboxar",
    shortLabel: "Laddbox",
    description: "Laddbox för hemmet inklusive installation.",
    icon: PlugZap,
    customerTypes: ["private", "business"],
    leadCategory: "laddbox",
    fields: [
      { id: "parking", label: "Var ska laddboxen sitta?", type: "select", options: ["Garage", "Carport", "Utomhus vid husvägg", "Stolpe på tomten"], required: true, businessOptions: ["Garage", "Carport", "Personalparkering", "Utomhus vid fasad", "Stolpe på fastigheten"] },
      { id: "boxes", label: "Antal laddboxar", type: "number", unit: "st", placeholder: "t.ex. 4", required: true, customerTypes: ["business"] },
      { id: "distance", label: "Avstånd från elcentral", type: "select", options: ["Under 10 meter", "10–25 meter", "Över 25 meter", "Vet ej"], required: true },
      { id: "power", label: "Önskad laddeffekt", type: "select", options: ["3,7 kW", "7,4 kW", "11 kW", "22 kW", "Vet ej"], required: true },
      { id: "smart", label: "Vill du ha smart styrning efter spotpris?", type: "radio", options: ["Ja", "Nej", "Vet ej"], customerTypes: ["private"] },
      { id: "needs", label: "Vad behöver ni utöver installationen?", type: "multi", options: ["Lastbalansering", "Betallösning", "Drift och service", "Bidragsansökan"], customerTypes: ["business"] },
      { id: "timeline", label: "När vill du installera?", type: "select", options: ["Snarast", "Inom 3 månader", "Inom 6 månader", "Bara nyfiken på pris"], required: true, businessLabel: "När vill ni installera?", businessOptions: ["Snarast", "Inom 3 månader", "Inom 6 månader", "Utreder pris"] },
    ],
  },
  {
    slug: "laddstolpar",
    label: "Laddstolpar",
    shortLabel: "Laddstolpar",
    description: "Laddinfrastruktur för BRF, samfällighet eller arbetsplats.",
    icon: CarFront,
    customerTypes: ["business", "private"],
    leadCategory: "laddstolpe",
    fields: [
      { id: "propertyType", label: "Typ av fastighet", type: "select", options: ["Villa", "Samfällighet", "Bostadsrättsförening", "Annat"], required: true, businessOptions: ["Bostadsrättsförening", "Samfällighet", "Arbetsplats/kontor", "Hyresfastighet", "Publik laddning"] },
      { id: "chargePoints", label: "Antal laddpunkter", type: "number", unit: "st", placeholder: "t.ex. 2", required: true, businessPlaceholder: "t.ex. 12" },
      { id: "parkingType", label: "Typ av parkering", type: "select", options: ["Utomhusparkering", "Garage", "Carport", "Blandat"], required: true },
      { id: "needs", label: "Vad behöver du?", type: "multi", options: ["Lastbalansering", "Drift och service", "Solceller i kombination"], businessLabel: "Vad behöver ni?", businessOptions: ["Lastbalansering", "Betallösning", "Drift och service", "Solceller i kombination", "Bidragsansökan"] },
      { id: "timeline", label: "När vill du installera?", type: "select", options: ["Snarast", "Inom 3 månader", "Inom 6 månader", "Bara nyfiken på pris"], required: true, businessLabel: "När vill ni installera?", businessOptions: ["Snarast", "Inom 3 månader", "Inom 6 månader", "Utreder pris"] },
    ],
  },
  {
    slug: "kommersiella-solceller",
    label: "Kommersiella solceller",
    shortLabel: "Kommersiella solceller",
    description: "Takanläggningar och markanläggningar för verksamheter.",
    icon: Factory,
    customerTypes: ["business"],
    leadCategory: "kommersiella_solceller",
    fields: [
      { id: "propertyType", label: "Typ av fastighet", type: "select", options: ["Industri/lager", "Kontor", "Lantbruk", "Handel", "Flerbostadshus", "Mark"], required: true },
      { id: "roofArea", label: "Ungefärlig takyta eller markyta", type: "number", unit: "m²", placeholder: "t.ex. 1200" },
      { id: "consumption", label: "Årsförbrukning", type: "number", unit: "kWh/år", placeholder: "t.ex. 250000", required: true },
      { id: "ownership", label: "Äger ni fastigheten?", type: "radio", options: ["Ja", "Nej, hyresgäst"], required: true },
      { id: "financing", label: "Önskad finansiering", type: "select", options: ["Köp", "Leasing", "Solel som tjänst (PPA)", "Vet ej"] },
      { id: "timeline", label: "När vill ni installera?", type: "select", options: ["Snarast", "Inom 3 månader", "Inom 6 månader", "Utreder pris"], required: true },
    ],
  },
  {
    slug: "kommersiella-batterier",
    label: "Kommersiella batterier",
    shortLabel: "Kommersiella batterier",
    description: "Batterilager för effekttoppar, stödtjänster och egenanvändning.",
    icon: BatteryFull,
    customerTypes: ["business"],
    leadCategory: "kommersiella_batterier",
    fields: [
      { id: "purpose", label: "Vad är syftet med batteriet?", type: "multi", options: ["Effekttoppskapning", "Stödtjänster (FCR)", "Öka egenanvändning av solel", "Reservkraft", "Undvika nätförstärkning"] },
      { id: "capacity", label: "Önskad kapacitet", type: "select", options: ["Under 100 kWh", "100–500 kWh", "500 kWh–1 MWh", "Över 1 MWh", "Vet ej – föreslå gärna"], required: true },
      { id: "gridPower", label: "Abonnerad effekt", type: "number", unit: "kW", placeholder: "t.ex. 400" },
      { id: "hasSolar", label: "Har ni solceller idag?", type: "radio", options: ["Ja", "Nej", "Planerar att installera"], required: true },
      { id: "timeline", label: "När vill ni installera?", type: "select", options: ["Snarast", "Inom 3 månader", "Inom 6 månader", "Utreder pris"], required: true },
    ],
  },
];

export const getQuoteService = (slug: string) => quoteServices.find((s) => s.slug === slug);

/** Anpassar ett fälts etikett, alternativ och placeholder efter kundtyp */
export const fieldForCustomerType = (field: QuoteField, type: CustomerType): QuoteField => {
  if (type !== "business") return field;
  return {
    ...field,
    ...(field.businessLabel ? { label: field.businessLabel } : {}),
    ...(field.businessOptions ? { options: field.businessOptions } : {}),
    ...(field.businessPlaceholder ? { placeholder: field.businessPlaceholder } : {}),
  };
};

/** Fälten som ska visas för en tjänst givet kundtyp */
export const fieldsForCustomerType = (service: QuoteService, type: CustomerType): QuoteField[] =>
  service.fields
    .filter((f) => !f.customerTypes || f.customerTypes.includes(type))
    .map((f) => fieldForCustomerType(f, type));

/** Alias-slugar (t.ex. lokala vertikaler) → tjänstens slug */
export const quoteServiceAliases: Record<string, string> = {
  batterier: "hembatterier",
  hembatteri: "hembatterier",
  varmepump: "varmepumpar",
  solcell: "solceller",
  laddbox: "laddboxar",
  laddstolpe: "laddstolpar",
};

export const resolveQuoteService = (slug?: string) =>
  slug ? getQuoteService(quoteServiceAliases[slug] ?? slug) : undefined;

/** Offertformulärets URL för en tjänst: /{tjanst}/offert */
export const quoteOfferPath = (slug: string) =>
  `/${quoteServiceAliases[slug] ?? slug}/offert`;
